import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, Flame, MessageCircle, Plane, Star, TrainFront } from "lucide-react";
import { IMAGES, WHATSAPP_URL } from "../data/tour";
import { openEnquire } from "./EnquireModal";
import { EASE } from "./Reveal";

const MaskedLine = ({ children, delay }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className="block"
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1.1, delay, ease: EASE }}
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1.05, 1.18]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section id="top" ref={ref} data-testid="hero-section" className="hero-grain relative flex min-h-screen items-end overflow-hidden bg-[#0A0D0A]">
      <motion.div className="absolute inset-0" style={{ y: bgY, scale: bgScale }}>
        <img
          src={IMAGES.hero}
          alt="Munnar tea gardens rolling into the mist"
          className="h-full w-full object-cover"
          data-testid="hero-image"
        />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-t from-[#0A0D0A] via-[#0A0D0A]/55 to-[#0A0D0A]/25" />

      <motion.div style={{ opacity: fade }} className="relative z-10 mx-auto w-full max-w-7xl px-6 pb-24 pt-40 lg:px-10">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.15 }}
          className="mb-8 flex flex-wrap items-center gap-3 text-[11px] uppercase tracking-[0.3em] text-[#B9C4BB]"
          data-testid="hero-departure-info"
        >
          <span className="flex items-center gap-2 rounded-full border border-[#2E382F] bg-[#121714]/60 px-3 py-1.5 backdrop-blur-sm">
            <TrainFront size={12} className="text-[#4ADE80]" /> Pune &amp; Mumbai — Every Friday
          </span>
          <span className="flex items-center gap-2 rounded-full border border-[#2E382F] bg-[#121714]/60 px-3 py-1.5 backdrop-blur-sm">
            <Plane size={12} className="text-[#4ADE80]" /> Flights — 27 Sept + Dec Sundays
          </span>
          <span className="flex items-center gap-2 rounded-full border border-[#2E382F] bg-[#121714]/60 px-3 py-1.5 backdrop-blur-sm">
            Kochi to Kochi — Every Sunday
          </span>
        </motion.div>

        <h1 className="font-serif text-[13vw] font-medium leading-[0.95] tracking-tight text-[#F4F1EB] sm:text-7xl lg:text-[6.5rem]" data-testid="hero-title">
          <MaskedLine delay={0.35}>Kerala</MaskedLine>
          <MaskedLine delay={0.5}>
            <span className="italic text-[#4ADE80]">with</span> Kanyakumari.
          </MaskedLine>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.9, ease: EASE }}
          className="mt-8 max-w-xl text-base leading-relaxed text-[#B9C4BB] sm:text-lg"
          data-testid="hero-subtitle"
        >
          Seven days through mist-clad tea gardens, backwater houseboats, cliff beaches and
          Kathakali nights — ending where three seas meet at the tip of India.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.0, ease: EASE }}
          className="mt-10 flex flex-wrap items-center gap-6"
          data-testid="hero-price-block"
        >
          <span className="flex items-center gap-2 rounded-full border border-[#4ADE80]/40 bg-[#121714]/60 px-4 py-2 text-[11px] font-bold uppercase tracking-[0.25em] text-[#4ADE80] backdrop-blur-sm">
            <Flame size={13} /> Limited Batches — Sept to Dec
          </span>
          <div>
            <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-[#B9C4BB]">Starting From</p>
            <p className="font-serif text-4xl font-medium tracking-tight text-[#F4F1EB]">
              ₹21,000
              <span className="ml-2 font-sans text-xs font-semibold uppercase tracking-[0.2em] text-[#B9C4BB]">+ 5% GST · Kochi to Kochi</span>
            </p>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.15, ease: EASE }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <button
            onClick={() => openEnquire()}
            data-testid="hero-book-cta"
            className="rounded-full bg-marigold px-9 py-4 text-xs font-bold uppercase tracking-[0.25em] text-[#0A0D0A] transition-colors duration-300 hover:bg-marigold-hover"
          >
            Enquire Now
          </button>
          <a
            href={WHATSAPP_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="hero-whatsapp-cta"
            className="flex items-center gap-2 rounded-full border border-[#F4F1EB]/30 px-9 py-4 text-xs font-bold uppercase tracking-[0.25em] text-[#F4F1EB] transition-colors duration-300 hover:border-[#4ADE80] hover:text-[#4ADE80]"
          >
            <MessageCircle size={14} /> Chat With Us
          </a>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.3, ease: EASE }}
          className="mt-8"
          data-testid="hero-rating-badge"
        >
          <div className="inline-flex flex-wrap items-center gap-3 rounded-full border border-[#2E382F] bg-[#121714]/60 px-5 py-3 backdrop-blur-sm">
            <svg viewBox="0 0 24 24" className="h-4 w-4" aria-hidden="true">
              <path fill="#4285F4" d="M23.49 12.27c0-.79-.07-1.54-.19-2.27H12v4.51h6.47a5.57 5.57 0 0 1-2.4 3.58v3h3.86c2.26-2.09 3.56-5.17 3.56-8.82z" />
              <path fill="#34A853" d="M12 24c3.24 0 5.95-1.08 7.93-2.91l-3.86-3c-1.08.72-2.45 1.16-4.07 1.16-3.13 0-5.78-2.11-6.73-4.96H1.29v3.09A11.99 11.99 0 0 0 12 24z" />
              <path fill="#FBBC05" d="M5.27 14.29A7.2 7.2 0 0 1 4.89 12c0-.8.14-1.57.38-2.29V6.62H1.29a12 12 0 0 0 0 10.76l3.98-3.09z" />
              <path fill="#EA4335" d="M12 4.75c1.77 0 3.35.61 4.6 1.8l3.42-3.42A11.98 11.98 0 0 0 12 0 11.99 11.99 0 0 0 1.29 6.62l3.98 3.09C6.22 6.86 8.87 4.75 12 4.75z" />
            </svg>
            <span className="flex items-center gap-0.5">
              {[...Array(5)].map((_, s) => (
                <Star key={s} size={13} className="fill-[#FBBC05] text-[#FBBC05]" />
              ))}
            </span>
            <span className="text-sm font-bold text-[#F4F1EB]">4.7</span>
            <span className="text-xs text-[#B9C4BB]">14,000+ Google reviews</span>
          </div>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 1 }}
        className="absolute bottom-8 right-8 z-10 hidden text-[#B9C4BB] md:block"
      >
        <motion.div animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 2.2, ease: "easeInOut" }}>
          <ArrowDown size={18} />
        </motion.div>
      </motion.div>
    </section>
  );
};
